import type { ReactNode } from 'react';
import { CheckCheck, Send, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/cn';

type Props = {
  bozze: number;
  /** Bozze con tutti i turni assegnati. */
  pronti: number;
  /** Bozze spuntate nell'elenco. */
  scelti: number;
  onProponi: () => void;
  onConfermaPronti: () => void;
  onConfermaScelti: () => void;
};

function Passo({ n, titolo, testo, evidenziato, children }: {
  n: number;
  titolo: string;
  testo: string;
  evidenziato?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col rounded-xl border bg-white p-4 shadow-sm', evidenziato ? 'border-primary-200' : 'border-gray-200')}>
      <div className="flex items-center gap-2.5">
        <span
          className={cn(
            'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-xs font-bold',
            evidenziato ? 'bg-primary-600 text-white' : 'bg-gray-100 text-neutral-700',
          )}
        >
          {n}
        </span>
        <h3 className="text-sm font-semibold text-gray-900">{titolo}</h3>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-neutral-500">{testo}</p>
      <div className="mt-auto pt-3">{children}</div>
    </div>
  );
}

/**
 * Come si assegna, in tre passi affiancati, ognuno con il suo pulsante. Solo
 * il terzo comunica qualcosa ai partecipanti.
 */
export function PassiAssegnazione({ bozze, pronti, scelti, onProponi, onConfermaPronti, onConfermaScelti }: Props) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <Passo n={1} titolo="Proponi" testo="Pixel dà a ognuno quello che ha chiesto, in ordine di arrivo, finché ci sono posti. Nessuna email parte.">
        <Button variant="outline" onClick={onProponi} className="w-full sm:w-auto">
          <Wand2 className="mr-2 h-4 w-4" />
          Proponi assegnazione
        </Button>
      </Passo>
      <Passo n={2} titolo="Ritocca" testo="Tocca un incontro nel tabellone per vedere chi c'è, oppure premi Modifica su una persona nell'elenco.">
        <p className="text-xs text-neutral-600">
          <span className="text-lg font-bold tabular-nums text-gray-900">{bozze}</span> {bozze === 1 ? 'persona in bozza' : 'persone in bozza'}
        </p>
      </Passo>
      <Passo n={3} titolo="Conferma e comunica" testo="Solo qui parte l'email con il percorso. Pronti = bozze con tutti i turni assegnati." evidenziato>
        <div className="flex flex-col gap-2 sm:flex-row lg:flex-col xl:flex-row">
          <Button variant="outline" onClick={onConfermaPronti} disabled={pronti === 0}>
            <CheckCheck className="mr-2 h-4 w-4" />
            Tutti i pronti ({pronti})
          </Button>
          <Button onClick={onConfermaScelti} disabled={scelti === 0} className="bg-primary-600 text-white shadow-lg shadow-primary-500/20 hover:bg-primary-700">
            <Send className="mr-2 h-4 w-4" />
            Gli spuntati ({scelti})
          </Button>
        </div>
      </Passo>
    </div>
  );
}
